"use client";

// Composer attachments UI (plan 023): the staged-tile strip above the
// composer frame, one tile per attachment, plus the transcript's own
// presentation for sent attachments (above the message bubble, like the
// flagship web app). The block never calls fetch itself — the host
// injects a `ComposerUploadsSource` that owns the wire (mint → PUT →
// finalize). `useMagnusUploads(chatId)` in `@bg-bgi/magnus-sdk/react`
// returns a ready-made source over the versioned REST endpoints.
//
// The MIME→icon map is inlined (not imported from `@workspace/file-types`)
// so the registry-distributed copy stays self-contained.

import type { SdkUploadFinalized } from "@bg-bgi/magnus-sdk";
import { Button } from "@workspace/ui/components/button";
import { FileProcessingIndicator } from "@workspace/ui/components/file-processing-indicator";
import { ScrollArea, ScrollBar } from "@workspace/ui/components/scroll-area";
import { cn } from "@workspace/ui/lib/utils";
import type { LucideIcon } from "lucide-react";
import {
  AlertCircleIcon,
  FileCodeIcon,
  FileIcon,
  FileSpreadsheetIcon,
  FileTextIcon,
  ImageIcon,
  Loader2Icon,
  PresentationIcon,
  RefreshCwIcon,
  XIcon,
} from "lucide-react";
import type { KeyboardEvent as ReactKeyboardEvent, ReactNode } from "react";
import type { EditorFilePart } from "./serialize";
import type { ComposerAttachment } from "./use-attachments";

// ---------------------------------------------------------------------------
// The host seam
// ---------------------------------------------------------------------------

/**
 * One processing-pipeline update for a finalized upload (C10, ADR-055).
 * `terminal` is source-computed (the block stays enum-free): true when
 * polling can stop and the composer's send gate lifts.
 */
export interface ComposerProcessingUpdate {
  richPassFailed?: boolean | null;
  status: SdkUploadFinalized["processingStatus"];
  terminal: boolean;
}

/**
 * Composer attachments source. Absent on the composer → no attach button,
 * no drag/drop, pastes stay inline. The source owns the whole upload
 * lifecycle; the block owns the staging state machine (`useAttachments`).
 *
 * Bind `upload` to the same chat id the block is driving.
 */
export interface ComposerUploadsSource {
  /** `<input accept>` values (MIME types + bare extensions). */
  accept: readonly string[];
  /**
   * Delete a finalized upload's row (and blobs). Called fire-and-forget
   * when a finalized tile is removed before send, so the server row does
   * not outlive the staging strip.
   */
  delete: (uploadId: string) => Promise<void>;
  /** Max staged attachments per message; extras are rejected with a toast. */
  maxFiles: number;
  /**
   * Mint → PUT → finalize, owned end-to-end by the source. Resolves the
   * finalized row (`url`/`storageKey`/`processingStatus`); rejects — and
   * cleans up the staged row — on failure. `signal` aborts the in-flight
   * PUT (tile remove / composer clear); `onProgress` maps to XHR progress
   * where the source supports it (the UI tolerates 0→100 jumps).
   */
  upload: (
    file: File,
    onProgress?: (percent: number) => void,
    signal?: AbortSignal
  ) => Promise<SdkUploadFinalized>;
  /**
   * Optional processing watch (ADR-055 text-ready gate). When present, the
   * composer subscribes per finalized tile, shows the live processing dot,
   * and gates submit until every staged upload reports `terminal: true`.
   * Return an unsubscribe. Absent → finalize-time snapshot only, no gate.
   */
  watchProcessing?: (
    uploadId: string,
    onUpdate: (update: ComposerProcessingUpdate) => void
  ) => () => void;
}

// ---------------------------------------------------------------------------
// Pure helpers (unit-tested without a renderer)
// ---------------------------------------------------------------------------

/** True when the file matches the accept list — by MIME type or by bare
 *  extension (the list carries both so `.xer`/`.md`/`.py` match). */
export function attachmentAcceptsFile(
  file: { name: string; type: string },
  accept: readonly string[]
): boolean {
  if (file.type && accept.includes(file.type)) {
    return true;
  }
  const dot = file.name.lastIndexOf(".");
  if (dot >= 0) {
    const ext = file.name.slice(dot).toLowerCase();
    if (accept.includes(ext)) {
      return true;
    }
  }
  return false;
}

export type AttachmentValidation =
  | { valid: true }
  | { valid: false; error: string };

/** Accept-list validation with the same message the private composer's
 *  `validateFile` produces for unsupported types. Size is enforced
 *  server-side at mint time (the seam carries no size limit). */
export function validateAttachment(
  file: File,
  accept: readonly string[]
): AttachmentValidation {
  if (attachmentAcceptsFile(file, accept)) {
    return { valid: true };
  }
  return {
    valid: false,
    error: `"${file.name}" has type "${file.type || "unknown"}" which is not supported.`,
  };
}

/** Remaining staging slots — the composer toasts and truncates overflow. */
export function attachmentCapacity(current: number, maxFiles: number): number {
  return Math.max(0, maxFiles - current);
}

/**
 * Build the message part for one staged attachment. Always the native AI
 * SDK `file` shape with the Magnus `data` payload — the server promotes
 * processable documents to `data-magnus-document` itself
 * (apps/web/app/api/chat/hydrate-document-parts.ts), so the block never
 * classifies.
 */
export function toAttachmentFilePart(
  attachment: ComposerAttachment
): EditorFilePart {
  const uploaded = attachment.uploaded;
  if (!uploaded) {
    throw new Error("toAttachmentFilePart requires an uploaded attachment");
  }
  return {
    type: "file",
    url: uploaded.url,
    mediaType: uploaded.mediaType,
    filename: uploaded.filename,
    data: {
      uploadId: uploaded.id,
      processingStatus: uploaded.processingStatus,
      size: attachment.file.size,
      storageKey: uploaded.storageKey,
    },
  };
}

/** Minimal MIME→icon map (registry-safe inline of `getFileIcon`). */
export function attachmentIcon(mimeType: string | undefined): LucideIcon {
  if (!mimeType) {
    return FileIcon;
  }
  if (mimeType.startsWith("image/")) {
    return ImageIcon;
  }
  if (
    mimeType.includes("spreadsheetml") ||
    mimeType === "text/csv" ||
    mimeType.includes("alteryx")
  ) {
    return FileSpreadsheetIcon;
  }
  if (mimeType.includes("presentationml")) {
    return PresentationIcon;
  }
  if (
    mimeType === "text/x-python" ||
    mimeType === "text/html" ||
    mimeType === "application/json" ||
    mimeType === "application/javascript" ||
    mimeType === "text/javascript" ||
    mimeType === "application/typescript" ||
    mimeType === "text/css" ||
    mimeType === "text/x-shellscript"
  ) {
    return FileCodeIcon;
  }
  if (
    mimeType === "application/pdf" ||
    mimeType.startsWith("text/") ||
    mimeType.includes("wordprocessingml") ||
    mimeType.includes("opendocument.text") ||
    mimeType === "application/rtf" ||
    mimeType === "application/epub+zip" ||
    mimeType === "application/xml" ||
    mimeType.endsWith("+xml")
  ) {
    return FileTextIcon;
  }
  return FileIcon;
}

/** Type-badge fill. Registry-safe inline of `getFileCategoryColor`, including
 *  its off-token format colours — see that function for why they stay literal. */
function attachmentColor(mimeType: string | undefined): string {
  if (!mimeType) {
    return "bg-muted-foreground";
  }
  if (mimeType === "application/pdf") {
    return "bg-red-600";
  }
  if (
    mimeType.includes("wordprocessingml") ||
    mimeType === "text/x-python" ||
    mimeType === "application/json" ||
    mimeType === "text/html"
  ) {
    return "bg-blue-600";
  }
  if (mimeType.startsWith("image/")) {
    return "bg-purple-600";
  }
  if (mimeType.includes("spreadsheetml") || mimeType === "text/csv") {
    return "bg-green-600";
  }
  if (mimeType.includes("presentationml")) {
    return "bg-orange-600";
  }
  if (mimeType.startsWith("text/")) {
    return "bg-sky-500";
  }
  return "bg-muted-foreground";
}

/** Short type label under the filename. Registry-safe inline of
 *  `getShortFileLabel`, covering the types the composer accepts. */
function attachmentLabel(mimeType: string | undefined): string {
  const LABELS: Record<string, string> = {
    "application/json": "JSON",
    "application/pdf": "PDF",
    "text/csv": "CSV",
    "text/html": "HTML",
    "text/markdown": "Markdown",
    "text/plain": "Text",
    "text/x-python": "Python",
    "application/javascript": "JavaScript",
    "text/javascript": "JavaScript",
    "application/typescript": "TypeScript",
    "text/css": "CSS",
    "text/x-shellscript": "Shell",
    "application/zip": "ZIP",
    "application/x-zip-compressed": "ZIP",
    "application/x-zip": "ZIP",
  };
  if (!mimeType) {
    return "File";
  }
  const exact = LABELS[mimeType];
  if (exact) {
    return exact;
  }
  if (mimeType.includes("wordprocessingml")) {
    return "Word";
  }
  if (mimeType.includes("spreadsheetml")) {
    return "Excel";
  }
  if (mimeType.includes("presentationml")) {
    return "PowerPoint";
  }
  if (mimeType.startsWith("image/")) {
    return mimeType.slice("image/".length).toUpperCase();
  }
  return "File";
}

// ---------------------------------------------------------------------------
// Attachment tile + scroll strips
// ---------------------------------------------------------------------------

/** Horizontal scroll strip for staged attachments, with edge fades. Sits
 *  above the composer frame (a sibling, not inside it), matching the
 *  flagship web composer. */
export function AttachmentsScroll({ children }: { children: ReactNode }) {
  return (
    <div className="relative mb-2 w-full px-3">
      <ScrollArea className="peer w-full">
        <div className="flex w-max items-start gap-2">{children}</div>
        <ScrollBar orientation="horizontal" />
      </ScrollArea>
      <div className="pointer-events-none absolute inset-y-0 left-3 z-10 w-8 bg-linear-to-r from-background to-transparent opacity-0 transition-opacity duration-200 peer-data-overflow-x-start:opacity-100" />
      <div className="pointer-events-none absolute inset-y-0 right-3 z-10 w-8 bg-linear-to-l from-background to-transparent opacity-0 transition-opacity duration-200 peer-data-overflow-x-end:opacity-100" />
    </div>
  );
}

/**
 * Horizontal scroll strip for sent attachments in the transcript, rendered
 * above the message bubble. Mirrors the flagship's `MessageAttachments`:
 * left-aligned for assistant messages, right-aligned for user messages via
 * the parent row's `is-user` marker class.
 */
export function MessageAttachmentsScroll({
  children,
}: {
  children: ReactNode;
}) {
  return (
    <div className="relative mb-2 w-full min-w-0 overflow-hidden">
      <ScrollArea className="peer w-full max-w-full">
        {/* Align with the message bubble: user uploads sit right, assistant
            files sit left. Driven by the parent `Message`'s `is-user`
            marker on its `group` element — same mechanism as the flagship. */}
        <div className="flex w-max items-start justify-start gap-2 pb-2 mr-auto group-[.is-user]:ml-auto group-[.is-user]:mr-0 group-[.is-user]:justify-end">
          {children}
        </div>
        <ScrollBar orientation="horizontal" />
      </ScrollArea>
      <div className="pointer-events-none absolute inset-y-0 left-0 z-10 w-8 bg-linear-to-r from-background to-transparent opacity-0 transition-opacity duration-200 peer-data-overflow-x-start:opacity-100" />
      <div className="pointer-events-none absolute inset-y-0 right-0 z-10 w-8 bg-linear-to-l from-background to-transparent opacity-0 transition-opacity duration-200 peer-data-overflow-x-end:opacity-100" />
    </div>
  );
}

/**
 * The flagship (plan 024) renders tiles that are BOTH activatable (open /
 * preview) and carry action buttons: staged tiles click-to-preview while
 * keeping remove/retry, and sent tiles open-on-click while keeping a
 * reprocess retry when a live processing pass fails. So `onActivate` is not
 * mutually exclusive with `onRemove`/`onRetry` — the tile mounts as a
 * `role="button"` wrapper around the nested action buttons (see the a11y
 * note on the wrapper below). Absent handlers simply hide their affordance.
 */
export type AttachmentTileProps = {
  /** MIME type — drives the icon and image-thumbnail detection. */
  mediaType?: string;
  /** File name: clamped on icon tiles, repeated in the hover strip. */
  name: string;
  /** Tile activation (open/preview the file). Absent → static tile. */
  onActivate?: () => void;
  /** Remove affordance (composer staging only). Absent → no button. */
  onRemove?: () => void;
  /** Re-run a failed upload/processing pass. Absent → no retry affordance. */
  onRetry?: () => void;
  /** Image thumbnail: an object URL while staged, the persisted URL in the
   *  transcript. */
  previewUrl?: string;
  /** Docling processing status for the bottom-right dot (post-finalize).
   *  Snapshot only — live polling stays a host concern (no status seam). */
  processingStatus?: string | null;
  /** Staging overlay state. Absent → no upload chrome (transcript tiles). */
  status?: "uploading" | "error";
};

/**
 * One attachment tile: image thumb (or icon + name), optional upload
 * spinner / error overlay with retry (composer staging), optional remove
 * button (composer staging), and the processing dot. The SAME tile renders
 * above the composer (staged) and above the transcript bubble (sent) — the
 * transcript passes no `status`/`onRemove`/`onRetry`, so those affordances
 * simply don't appear, matching the flagship's read-only transcript tiles.
 */
export function AttachmentTile({
  mediaType,
  name,
  onActivate,
  onRemove,
  onRetry,
  previewUrl,
  processingStatus,
  status,
}: AttachmentTileProps) {
  const isImage = !!mediaType?.startsWith("image/");
  const Icon = attachmentIcon(mediaType);

  const inner = (
    <>
      {isImage && previewUrl ? (
        // biome-ignore lint/performance/noImgElement: plain <img> — the block is host-agnostic and has no image component seam (the private tile uses the host's `useMagnusMedia`).
        <img
          alt={name}
          className="absolute inset-0 size-full object-cover"
          src={previewUrl}
        />
      ) : (
        <div className="flex size-full flex-col items-center justify-center gap-1 bg-muted p-1.5">
          <Icon className="size-5 shrink-0 text-muted-foreground" />
          <span className="w-full text-center text-[10px] leading-tight text-muted-foreground line-clamp-2 break-all">
            {name}
          </span>
        </div>
      )}

      {status === "uploading" && (
        <div className="absolute inset-0 flex items-center justify-center rounded-lg bg-background/60 backdrop-blur-sm">
          <Loader2Icon className="size-6 animate-spin text-primary" />
        </div>
      )}

      {status === "error" && (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-1 rounded-lg bg-background/80 backdrop-blur-sm">
          <AlertCircleIcon className="size-5 text-destructive" />
          {onRetry && (
            <Button
              aria-label="Retry upload"
              className="h-6 px-2 text-xs"
              onClick={(e) => {
                e.stopPropagation();
                onRetry();
              }}
              size="sm"
              type="button"
              variant="outline"
            >
              <RefreshCwIcon className="size-3 mr-1" />
              Retry
            </Button>
          )}
        </div>
      )}

      {onRemove && (
        <Button
          aria-label="Remove file"
          className="absolute top-1 right-1 size-5 rounded-full bg-background/80 p-0 opacity-100 backdrop-blur-sm transition-opacity hover:bg-background sm:opacity-0 sm:group-hover:opacity-100 [&>svg]:size-3"
          onClick={(e) => {
            e.stopPropagation();
            onRemove();
          }}
          type="button"
          variant="ghost"
        >
          <XIcon />
        </Button>
      )}

      {/* Null-safe (returns null for null/undefined/"completed"), so the
          composer only shows the dot once `uploaded` lands and transcript
          tiles only when the part carries a status. The dot doubles as the
          retry affordance on a `failed` status — the overlay above only
          appears for a failed UPLOAD, so without this a failed processing
          pass has no way back (the flagship retried from the dot). */}
      <FileProcessingIndicator
        className="absolute bottom-1.5 right-1.5 z-10"
        onRetry={onRetry}
        retryClassName="absolute bottom-1 right-1 z-10"
        status={processingStatus}
      />

      <div className="pointer-events-none absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-1.5 opacity-100 transition-opacity sm:opacity-0 sm:group-hover:opacity-100">
        <span className="block truncate text-[10px] text-white">{name}</span>
      </div>
    </>
  );

  const wrapperClassName = cn(
    "group relative size-20 shrink-0 overflow-hidden rounded-lg",
    status === "error" && "ring-2 ring-destructive",
    onActivate && "cursor-pointer"
  );

  if (onActivate) {
    return (
      // biome-ignore lint/a11y/useSemanticElements: the tile can contain nested action buttons (remove/retry), so it can't itself be a <button>; role="button" is the accessible compromise.
      <div
        className={wrapperClassName}
        onClick={onActivate}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onActivate();
          }
        }}
        role="button"
        tabIndex={0}
      >
        {inner}
      </div>
    );
  }

  return <div className={wrapperClassName}>{inner}</div>;
}

export type SentAttachmentTileProps = {
  mediaType?: string;
  name: string;
  /** Opens the host's preview. Absent → the tile is static. */
  onActivate?: () => void;
  /** Retries a failed processing pass, offered from the status dot. */
  onRetry?: () => void;
  previewUrl?: string;
  processingStatus?: string | null;
};

/**
 * A sent attachment, rendered above the message bubble in the transcript.
 * Deliberately not the composer's staged tile: a staged tile is a dense grid
 * cell built for editing (remove button, upload progress, filename crammed
 * under a gradient), while a sent one is a settled record of what the user
 * attached. So images get a plain square thumbnail and everything else a
 * labeled card — the flagship transcript's treatment.
 */
export function SentAttachmentTile({
  mediaType,
  name,
  onActivate,
  onRetry,
  previewUrl,
  processingStatus,
}: SentAttachmentTileProps) {
  const indicator = (
    <FileProcessingIndicator
      className="absolute right-1.5 bottom-1.5 z-10"
      onRetry={onRetry}
      retryClassName="absolute right-1 bottom-1 z-10"
      status={processingStatus}
    />
  );

  // A `role="button"` wrapper rather than a real <button>: the tile nests its
  // own retry button, which a button can't legally contain.
  const activation = onActivate
    ? {
        onClick: onActivate,
        onKeyDown: (e: ReactKeyboardEvent<HTMLDivElement>) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onActivate();
          }
        },
        role: "button" as const,
        tabIndex: 0,
      }
    : {};

  if (mediaType?.startsWith("image/") && previewUrl) {
    return (
      <div
        className={cn(
          "group relative size-24 shrink-0 overflow-hidden rounded-lg",
          onActivate && "cursor-pointer"
        )}
        {...activation}
      >
        {/* biome-ignore lint/performance/noImgElement: plain <img> — the block is host-agnostic and has no image component seam. */}
        <img
          alt={name || "attachment"}
          className="size-full object-cover"
          src={previewUrl}
        />
        {indicator}
      </div>
    );
  }

  const Icon = attachmentIcon(mediaType);
  return (
    <div
      className={cn(
        "group relative flex shrink-0 items-center gap-3 rounded-xl border border-border/60 bg-secondary/50 px-3 py-2.5 transition-colors",
        onActivate && "cursor-pointer hover:bg-secondary/80"
      )}
      {...activation}
    >
      {indicator}
      <div
        className={cn(
          "flex size-10 shrink-0 items-center justify-center rounded-lg text-white",
          attachmentColor(mediaType)
        )}
      >
        <Icon className="size-5" />
      </div>
      <div className="min-w-0 flex-1">
        <p className="truncate font-medium text-sm leading-tight">
          {name || "Attachment"}
        </p>
        <p className="text-muted-foreground text-xs">
          {attachmentLabel(mediaType)}
        </p>
      </div>
    </div>
  );
}
